import { useState } from "react";
import { toast } from "sonner";

import { uploadProjectPhoto } from "@/api/actions/project-log";
import { dropDetachedViews, VIEW_KEY } from "@/lib/query";
import { photoSizeBlockReason } from "@buildpy/core/modules/project/photo";
import { resizeImage } from "@/modules/project/ui/resize-image";
import { useRecordAction } from "@/modules/project/ui/use-record-action";

export interface UploadProgress {
  done: number;
  total: number;
}

// 사진·일지·댓글이 바꾸는 다른 화면의 읽기 — 상세(최근 사진·일지)·오늘 기록(모든 날짜)·직원 오늘·현장 목록(오늘 신호).
export const photoViewKeys = (projectId: string) =>
  [
    [VIEW_KEY, "project", { id: projectId }],
    [VIEW_KEY, "project-today", { id: projectId }],
    [VIEW_KEY, "staff-today"],
    [VIEW_KEY, "projects"],
  ] as const;

// 사진 여러 장 올리기 — 한 장씩 줄여서 차례로 올리고 올린 장 수를 돌려준다(오늘 기록·촬영 화면이 같이 쓴다).
export const usePhotoUpload = (projectId: string, logDate: string) => {
  const { run } = useRecordAction();
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const uploadAll = async (list: FileList | null, phaseId: string | null): Promise<number> => {
    const files = list ? Array.from(list) : [];
    if (files.length === 0) return 0;
    let ok = 0;
    setProgress({ done: 0, total: files.length });
    try {
      for (const [index, file] of files.entries()) {
        let blob: Blob;
        try {
          blob = await resizeImage(file);
        } catch {
          toast.error(`${index + 1}번째 사진을 열 수 없습니다`);
          setProgress({ done: index + 1, total: files.length });
          continue;
        }
        // 줄인 뒤에도 크면 서버 한도(본문 크기)에 막히기 전에 여기서 알린다.
        const sizeReason = photoSizeBlockReason(blob.size);
        if (sizeReason) {
          toast.error(`${index + 1}번째 사진 — ${sizeReason}`);
          setProgress({ done: index + 1, total: files.length });
          continue;
        }
        const form = new FormData();
        form.append("file", blob, "photo.jpg");
        form.append("log_date", logDate);
        if (phaseId) form.append("project_phase_id", phaseId);
        const outcome = await run("사진을 올리지 못했습니다", () => uploadProjectPhoto(projectId, form));
        if (outcome === "moved") break;
        if (outcome === "done") {
          ok += 1;
          dropDetachedViews(photoViewKeys(projectId));
        }
        setProgress({ done: index + 1, total: files.length });
      }
    } finally {
      setProgress(null);
    }
    if (ok > 0) toast.success(`사진 ${ok}장을 올렸습니다`);
    return ok;
  };

  return { progress, uploadAll };
};
